import {
	AssignmentOutlined as AssignmentIcon,
	PersonOutlined as PersonIcon,
	PeopleOutlineOutlined as PeopleIcon
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { UserInformationViewModel } from "../../ViewModels/UserInformationViewModel";
import { useProjectFilter } from "../../contexts/ProjectFilterContext";
import { useClient } from "../../hooks/useClient";
import { useProjectStatuses } from "../../hooks/useProject";
import { User } from "../../models/User";
import { UserType } from "../../models/UserType";
import Menu, { MenuItem } from "./Menu";
import MenuSkeleton from "./MenuSkeleton";
import { generateProjectStatusMenuItems } from "../../utils/menuHelpers";
import { CLIENT_TALENTS_ENABLED } from "../../utils/featureFlags";

interface Props {
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	drawerWidth: number;
	user: User;
}

/**
 * Menu component for client users
 * Only shows projects menu item if onboarding is completed
 */
const ClientMenu: React.FC<Props> = ({
	isCollapsed,
	setIsCollapsed,
	drawerWidth,
	user
}) => {
	const { data: clientProfile, isLoading: profileLoading } = useClient();
	const { data: projectStatuses, isLoading: statusesLoading } = useProjectStatuses();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { statusFilter: currentStatusFilter } = useProjectFilter();

	// Helper function to get menu item key from current route
	const getMenuItemFromRoute = (pathname: string, search: string) => {
		if (pathname.startsWith("/profile")) {
			return "myProfile";
		} else if (pathname === "/projects/create") {
			return "createProject";
		} else if (pathname.match(/^\/projects\/\d+$/)) {
			// If we're on a project detail page, read status from URL params
			const params = new URLSearchParams(search);
			const status = params.get("status");
			if (status === "Draft") {
				return "draftProjects";
			} else if (status === "Submitted") {
				return "submittedProjects";
			}
			return "allProjects";
		} else if (pathname === "/projects") {
			// On projects list, determine active item from URL params
			const params = new URLSearchParams(search);
			const status = params.get("status");
			if (status === "Draft") {
				return "draftProjects";
			} else if (status === "Submitted") {
				return "submittedProjects";
			}
			return "allProjects";
		} else if (pathname.startsWith("/talents")) {
			return "matchedTalents";
		}
		return "myProfile"; // Default fallback
	};

	const getInitialActiveMenuItem = () => {
		const routeBasedItem = getMenuItemFromRoute(location.pathname, location.search);
		const storageKey = `zoulloo_client_menu_active_item_user_${user.id}`;
		const storedItem = sessionStorage.getItem(storageKey);

		// If we're on a detail page, keep the stored item
		if (routeBasedItem === null && storedItem) {
			return storedItem;
		}

		if (!storedItem && routeBasedItem !== null) {
			sessionStorage.setItem(storageKey, routeBasedItem);
			return routeBasedItem;
		}

		return storedItem || "allProjects";
	};

	const [activeMenuItem, setActiveMenuItem] = useState(getInitialActiveMenuItem());

	const [userInformationViewModel, setUserInformationViewModel] =
		useState<UserInformationViewModel>({
			userId: user.id || 0,
			firstName: user.firstName || "",
			lastName: user.lastName || "",
			type: t(`enums.userType.${user.type}`),
			profilePhotoUrl: ""
		});

	useEffect(() => {
		if (user) {
			setUserInformationViewModel({
				userId: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				type: t(`enums.userType.${user.type}`),
				profilePhotoUrl: ""
			});
		}
	}, [user, t]);

	// Track if we're navigating from menu click
	const [isMenuNavigation, setIsMenuNavigation] = useState(false);

	// Listen for route changes and update active menu item
	useEffect(() => {
		// If this navigation was triggered by menu click, skip the auto-update
		if (isMenuNavigation) {
			setIsMenuNavigation(false);
			return;
		}

		const newActiveItem = getMenuItemFromRoute(location.pathname, location.search);

		// Only update if we're not on a detail page (null means detail page)
		if (newActiveItem !== null && newActiveItem !== activeMenuItem) {
			const storageKey = `zoulloo_client_menu_active_item_user_${user.id}`;
			sessionStorage.setItem(storageKey, newActiveItem);
			setActiveMenuItem(newActiveItem);
		}
		// If we're on a detail page, don't change the active menu item
	}, [location.pathname, location.search]);

	// Sync active menu item with current filter when on projects page
	useEffect(() => {
		if (location.pathname === "/projects") {
			let newActiveItem = "allProjects";
			if (currentStatusFilter === "Draft") {
				newActiveItem = "draftProjects";
			} else if (currentStatusFilter === "Submitted") {
				newActiveItem = "submittedProjects";
			} else if (currentStatusFilter === "") {
				newActiveItem = "allProjects";
			}

			if (newActiveItem !== activeMenuItem) {
				setActiveMenuItem(newActiveItem);
			}
		}
	}, [currentStatusFilter, location.pathname]);

	const handleMenuItemClick = (menuKey: string, navigationPath: string) => {
		const storageKey = `zoulloo_client_menu_active_item_user_${user.id}`;

		// Mark this as a menu navigation
		setIsMenuNavigation(true);

		sessionStorage.setItem(storageKey, menuKey);

		setActiveMenuItem(menuKey);

		navigate(navigationPath);
	};

	// Check if onboarding is completed
	const onboardingCompleted = clientProfile?.onboardingCompleted;

	const mainMenuItems: MenuItem[] = [
		{
			key: "myProfile",
			text: t("menu.myProfile"),
			icon: <PersonIcon fontSize="medium" />,
			onClick: () => handleMenuItemClick("myProfile", "/profile")
		}
	];

	// Only show projects menu item if onboarding is completed
	if (onboardingCompleted) {
		// Generate status menu items dynamically
		const statusMenuItems = projectStatuses
			? generateProjectStatusMenuItems(projectStatuses, t, navigate, "/projects")
			: [];

		mainMenuItems.push({
			key: "myProjects",
			text: t("menu.myProjects"),
			icon: <AssignmentIcon fontSize="medium" />,
			activateClickOnActiveItem: true,
			onClick: () => handleMenuItemClick("myProjects", "/projects"),
			submenu: [
				{
					key: "createProject",
					text: t("menu.createProject"),
					icon: null,
					activateClickOnActiveItem: true,
					onClick: () => {
						handleMenuItemClick("createProject", "/projects/create");
					}
				},
				...statusMenuItems
			]
		});

		// "Mes talents suggérés" — anonymized matched talents (Option A).
		if (CLIENT_TALENTS_ENABLED) {
			mainMenuItems.push({
				key: "matchedTalents",
				text: t("anonymizedTalent.menuMatchedTalents"),
				icon: <PeopleIcon fontSize="medium" />,
				onClick: () => handleMenuItemClick("matchedTalents", "/talents")
			});
		}
	}

	// Check if user is company type or statuses are loading
	if (profileLoading || statusesLoading) {
		return <MenuSkeleton isCollapsed={isCollapsed} drawerWidth={drawerWidth} />;
	}

	if (!user || user.type !== UserType.Client) {
		return (
			<Box display="flex" justifyContent="center" height="100vh">
				<Typography color="error">{t("error.accessDeniedMenu")}</Typography>
			</Box>
		);
	}

	return (
		<Menu
			variant="permanent"
			isCollapsed={isCollapsed}
			setIsCollapsed={setIsCollapsed}
			drawerWidth={drawerWidth}
			defaultActiveItem={activeMenuItem}
			userInformationViewModel={userInformationViewModel}
			lastActiveMenuStorageKey={`zoulloo_client_menu_active_item_user_${user.id}`}
			mainMenuItems={mainMenuItems}
		/>
	);
};

export default ClientMenu;
